import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, auth } from './auth-context';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
    Menu,
    X,
    ArrowLeft,
    ChartBar,
    Brain,
    Target,
    LogOut,
    Eye,
    EyeOff,
    Zap,
    Crown,
    AlertTriangle,
    Info
} from 'lucide-react';

import { SecurityOperationsQuestions } from './Questions/security-operations-questions';
import { GeneralSecurityConceptsQuestions } from './Questions/general-security-concepts-questions';
import { ThreatsVulnerabilitiesMitigationsQuestions } from './Questions/threats-vulnerabilities-mitigations-questions';
import { ProgramManagementQuestions } from './Questions/program-management-questions';
import { SecurityArchitectureQuestions } from './Questions/security-architecture-questions';
import { fetchUserStats, updateUserStats, updateDomainMastery, updateAbilityEstimate, logResponse, fetchItemHistory, updateItemHistory, fetchDailyPracticeState, updateDailyPracticeState, defaultDomainMastery } from './firebase-utils';
import PracticeModeSelection from './practice-mode-selection';
import {
    processResponse as eapProcessResponse,
    selectNextItem,
    classifyResponse,
    difficultyToLogit,
    createDefaultAbilityEstimate,
    migrateFromDeltaRule
} from './lib/eap-estimator';
import { updateMasteryWithDecay } from './lib/mastery-decay';
import {
    createNewItemRecord,
    deriveRating,
    updateItemRecord,
    countDueItems,
    getDueItems,
    selectNextReviewItem
} from './lib/fsrs-scheduler';

// Custom modal to replace window.confirm / window.alert
const Modal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'OK', cancelText, icon = 'info' }) => {
    if (!isOpen) return null;
    const IconComponent = icon === 'warning' ? AlertTriangle : Info;
    const iconColor = icon === 'warning' ? 'text-orange-400' : 'text-blue-400';
    const iconBg = icon === 'warning' ? 'bg-orange-500/10' : 'bg-blue-500/10';

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="rounded-2xl bg-[#0B1224] ring-1 ring-white/10 max-w-sm w-full p-6 shadow-2xl">
                <div className="flex items-start space-x-4 mb-5">
                    <div className={`h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`h-5 w-5 ${iconColor}`} />
                    </div>
                    <div>
                        <h3 className="text-white font-semibold mb-1">{title}</h3>
                        <p className="text-sm text-white/55">{message}</p>
                    </div>
                </div>
                <div className="flex justify-end space-x-3">
                    {cancelText && (
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 rounded-lg ring-1 ring-white/10 text-white/70 hover:bg-white/10 text-sm transition-colors"
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PracticeTest = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null, confirmText: 'OK', cancelText: null, icon: 'info' });
    const showModal = (opts) => setModal({ isOpen: true, ...opts });
    const closeModal = () => setModal(prev => ({ ...prev, isOpen: false }));

    const { user, isPaidUser } = useAuth()
    const allQuestions = [
        ...SecurityOperationsQuestions,
        ...GeneralSecurityConceptsQuestions,
        ...ThreatsVulnerabilitiesMitigationsQuestions,
        ...ProgramManagementQuestions,
        ...SecurityArchitectureQuestions
    ]

    // UI State
    const [isTimerVisible, setIsTimerVisible] = useState(true);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showNextButton, setShowNextButton] = useState(false);

    // Test State
    const [selectedMode, setSelectedMode] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [answeredQuestions, setAnsweredQuestions] = useState([]);
    const [missedQuestions, setMissedQuestions] = useState([]);
    const [questionHistory, setQuestionHistory] = useState(new Set());

    // Response timing
    const [questionStartTime, setQuestionStartTime] = useState(null);

    // User Progress — EAP-based ability estimation
    const [abilityEstimate, setAbilityEstimate] = useState(createDefaultAbilityEstimate());
    const [lastPracticedByDomain, setLastPracticedByDomain] = useState({});
    const [itemHistory, setItemHistory] = useState({});
    const [dueItemCount, setDueItemCount] = useState(0);
    const [domainMastery, setDomainMastery] = useState({
        'Security Operations': 0,
        'General Security Concepts': 0,
        'Threats, Vulnerabilities and Mitigations': 0,
        'Security Program Management': 0,
        'Security Architecture': 0
    });

    // Daily quiz limit (free users get 1 quiz session per day)
    const [dailyQuizUsed, setDailyQuizUsed] = useState(false);

    // Timing
    const [sessionStartTime, setSessionStartTime] = useState(null);
    const [sessionStats, setSessionStats] = useState({
        correct: 0,
        total: 0,
        streak: 0,
        remainingQuestions: 0,
        timeRemaining: null,
        questionPool: []
    });

    const domainWeights = {
        'Security Operations': 0.28,
        'General Security Concepts': 0.12,
        'Threats, Vulnerabilities and Mitigations': 0.22,
        'Security Program Management': 0.20,
        'Security Architecture': 0.18
    };

    // NOTE: calculateProbability and updateAbility (delta rule) have been replaced
    // by the Bayesian EAP estimator in lib/eap-estimator.js.
    // Ability updates now go through eapProcessResponse() which maintains
    // a full posterior distribution and converges properly.

    const updateDomainMasteryWithPersistence = async (domain, correct, difficulty) => {
        // Apply decay before updating (Spec 5: mastery decay)
        const { newMastery: updatedValue, newLastPracticed: lastPracticedTs } = updateMasteryWithDecay(
            domainMastery[domain],
            lastPracticedByDomain[domain],
            correct,
            difficulty
        );

        const newMastery = { ...domainMastery, [domain]: updatedValue };
        const updatedLastPracticed = { ...lastPracticedByDomain, [domain]: lastPracticedTs };

        setDomainMastery(newMastery);
        setLastPracticedByDomain(updatedLastPracticed);

        if (user) {
            await updateDomainMastery(user.uid, newMastery, updatedLastPracticed);
        }
    };

    const selectWeightedQuestions = (totalQuestions, mode = null, domain = null) => {
        let availableQuestions = [...allQuestions];

        if (mode === 'Missed') {
            availableQuestions = allQuestions.filter(q =>
                missedQuestions.includes(q.id)
            );
            if (availableQuestions.length === 0) {
                showModal({ title: 'No Questions', message: 'No missed questions available. Try another mode!', onConfirm: closeModal });
                return [];
            }
            totalQuestions = Math.min(totalQuestions, availableQuestions.length);
        } else if (mode === 'Domain' && domain) {
            availableQuestions = allQuestions.filter(q => q.domain === domain);
            if (availableQuestions.length === 0) {
                showModal({ title: 'No Questions', message: 'No questions available for this domain. Try another one!', onConfirm: closeModal });
                return [];
            }
        }

        if (mode !== 'Missed') {
            const questionsByDomain = {};
            availableQuestions.forEach(question => {
                if (!questionsByDomain[question.domain]) {
                    questionsByDomain[question.domain] = [];
                }
                questionsByDomain[question.domain].push(question);
            });

            const selectedQuestions = [];
            if (domain) {
                // For domain-specific practice, just select from that domain
                const domainQuestions = questionsByDomain[domain]?.filter(q => !questionHistory.has(q.id)) || [];
                const shuffled = [...domainQuestions].sort(() => Math.random() - 0.5);
                selectedQuestions.push(...shuffled.slice(0, totalQuestions));
            } else {
                // For other modes, use weighted selection
                const questionCounts = {};
                Object.entries(domainWeights).forEach(([d, weight]) => {
                    questionCounts[d] = Math.round(totalQuestions * weight);
                });

                Object.entries(questionCounts).forEach(([d, count]) => {
                    const availableForDomain = questionsByDomain[d]?.filter(q => !questionHistory.has(q.id)) || [];
                    const shuffled = [...availableForDomain].sort(() => Math.random() - 0.5);
                    selectedQuestions.push(...shuffled.slice(0, count));
                });
            }

            const newHistory = new Set([...questionHistory]);
            selectedQuestions.forEach(q => newHistory.add(q.id));
            setQuestionHistory(newHistory);

            return selectedQuestions;
        }

        // For missed questions mode, just return shuffled available questions
        return availableQuestions
            .sort(() => Math.random() - 0.5)
            .slice(0, totalQuestions);
    };


    const selectNextQuestion = useCallback(() => {
        const answeredIds = answeredQuestions.map(aq => aq.id);
        return selectNextItem(
            sessionStats.questionPool,
            answeredIds,
            abilityEstimate,
            selectedMode,
            answeredQuestions.length
        );
    }, [sessionStats.questionPool, answeredQuestions, selectedMode, abilityEstimate]);

    const handleAnswer = async (selectedOption) => {
        // Prevent double answers
        if (!currentQuestion || showNextButton) return;

        // Capture response time
        const responseTimeMs = questionStartTime ? performance.now() - questionStartTime : null;

        const correct = selectedOption === currentQuestion.correctAnswer;

        // EAP ability update (replaces delta rule)
        const diffLogit = currentQuestion.difficulty_logit ?? difficultyToLogit(currentQuestion.difficulty);
        const updatedEstimate = eapProcessResponse(
            abilityEstimate,
            currentQuestion.domain,
            diffLogit,
            correct
        );
        setAbilityEstimate(updatedEstimate);

        // Persist ability estimate
        if (user) {
            updateAbilityEstimate(user.uid, updatedEstimate);
        }

        // Classify response into diagnostic quadrant
        const quadrant = responseTimeMs
            ? classifyResponse(correct, responseTimeMs, currentQuestion.difficulty)
            : (correct ? 'fluent' : 'gap');

        // Log individual response for analytics
        if (user) {
            logResponse(user.uid, {
                itemId: currentQuestion.id,
                domain: currentQuestion.domain,
                correct,
                selectedOption,
                responseTimeMs: responseTimeMs ? Math.round(responseTimeMs) : null,
                quadrant,
                mode: selectedMode
            });
        }

        // FSRS: Update item history for spaced repetition
        const existingRecord = itemHistory[currentQuestion.id]
            || createNewItemRecord(currentQuestion.id, currentQuestion.domain);
        const rating = deriveRating(correct, responseTimeMs, currentQuestion.difficulty);
        const updatedRecord = updateItemRecord(existingRecord, rating);
        const newItemHistory = { ...itemHistory, [currentQuestion.id]: updatedRecord };
        setItemHistory(newItemHistory);
        setDueItemCount(countDueItems(newItemHistory));

        if (user) {
            updateItemHistory(user.uid, currentQuestion.id, updatedRecord);
        }

        await updateDomainMasteryWithPersistence(currentQuestion.domain, correct, currentQuestion.difficulty);

        // Update missed questions tracking
        if (!correct && !missedQuestions.includes(currentQuestion.id)) {
            const newMissedQuestions = [...missedQuestions, currentQuestion.id];
            setMissedQuestions(newMissedQuestions);
            if (user) {
                try {
                    await updateUserStats(user.uid, { missedQuestions: newMissedQuestions });
                } catch (error) {
                    console.error('Error updating missed questions:', error);
                }
            }
        } else if (correct && missedQuestions.includes(currentQuestion.id)) {
            const newMissedQuestions = missedQuestions.filter(id => id !== currentQuestion.id);
            setMissedQuestions(newMissedQuestions);
            if (user) {
                try {
                    await updateUserStats(user.uid, { missedQuestions: newMissedQuestions });
                } catch (error) {
                    console.error('Error updating missed questions:', error);
                }
            }
        }

        // Always show explanation after answering
        setShowExplanation(true);

        const newTotal = sessionStats.total + 1;
        setSessionStats(prev => ({
            ...prev,
            correct: prev.correct + (correct ? 1 : 0),
            total: newTotal,
            streak: correct ? prev.streak + 1 : 0,
            remainingQuestions: prev.remainingQuestions > 0 ? prev.remainingQuestions - 1 : 0
        }));

        setAnsweredQuestions(prev => [...prev, {
            ...currentQuestion,
            userAnswer: selectedOption,
            correct
        }]);

        // Always show next button after answering
        setShowNextButton(true);
    };

    const handleNextQuestion = () => {
        const nextQuestion = selectNextQuestion();
        if (nextQuestion) {
            setCurrentQuestion(nextQuestion);
            setQuestionStartTime(performance.now());
            setShowNextButton(false);
            setShowExplanation(false);
        } else {
            showModal({
                title: 'Session Complete',
                message: 'You have completed all questions in this session. Would you like to end it?',
                confirmText: 'End Session',
                cancelText: 'Cancel',
                onConfirm: () => { closeModal(); resetSession(); },
                onCancel: closeModal
            });
        }
    };

    const resetSession = async () => {
        if (user && sessionStats.total > 0) {
            const sessionData = {
                correctAnswers: sessionStats.correct,
                totalQuestions: sessionStats.total,
                timeSpent: selectedMode === 'Exam' ?
                    90 * 60 - sessionStats.timeRemaining :
                    Math.floor(Date.now() / 1000) - sessionStartTime,
                domainMastery,
                mode: selectedMode,
                score: (sessionStats.correct / sessionStats.total) * 100
            };

            try {
                await updateUserStats(user.uid, sessionData);
            } catch (error) {
                console.error('Error saving session:', error);
            }

            // Mark daily quiz as used for free users
            if (!isPaidUser && selectedMode === 'Quiz') {
                const todayStr = new Date().toLocaleDateString('en-CA');
                setDailyQuizUsed(true);
                if (user) {
                    updateDailyPracticeState(user.uid, { date: todayStr, quizUsed: true });
                }
            }
        }

        setSelectedMode(null);
        setCurrentQuestion(null);
        setSessionStats({
            correct: 0,
            total: 0,
            streak: 0,
            remainingQuestions: 0,
            timeRemaining: null,
            questionPool: []
        });
    };

    const startQuizMode = (mode, domain = null) => {
        setSessionStartTime(Math.floor(Date.now() / 1000));
        setSelectedMode(mode);
        setSelectedDomain(domain);
        setShowNextButton(false);
        setShowExplanation(false);
        setAnsweredQuestions([]);

        // Special handling for Review mode (FSRS spaced repetition)
        if (mode === 'Review') {
            const dueItems = getDueItems(itemHistory);
            if (dueItems.length === 0) {
                showModal({ title: 'Nothing to Review', message: 'No items due for review right now. Practice some questions first!', onConfirm: closeModal });
                setSelectedMode(null);
                return;
            }
            // Build a pool from due items by looking up the full question objects
            const dueQuestionPool = dueItems
                .map(record => allQuestions.find(q => q.id === record.itemId))
                .filter(Boolean);

            if (dueQuestionPool.length === 0) {
                showModal({ title: 'No Questions', message: 'No review items found. Try another mode!', onConfirm: closeModal });
                setSelectedMode(null);
                return;
            }

            setSessionStats({
                correct: 0,
                total: 0,
                streak: 0,
                remainingQuestions: dueQuestionPool.length,
                timeRemaining: null,
                questionPool: dueQuestionPool
            });

            setCurrentQuestion(dueQuestionPool[0]);
            setQuestionStartTime(performance.now());
            return;
        }

        let questionCount;
        let timer = null;

        switch (mode) {
            case 'Quiz':
                questionCount = 20;
                break;
            case 'Exam':
                questionCount = 90;
                timer = 90 * 60;
                break;
            case 'Unlimited':
                questionCount = 50;
                break;
            case 'Missed':
                questionCount = Math.min(20, missedQuestions.length);
                break;
            case 'Domain':
                questionCount = 20;
                break;
            default:
                questionCount = 20;
        }

        const questionPool = selectWeightedQuestions(questionCount, mode, domain);

        if (questionPool.length === 0) {
            setSelectedMode(null);
            setSelectedDomain(null);
            return;
        }

        setSessionStats({
            correct: 0,
            total: 0,
            streak: 0,
            remainingQuestions: questionCount,
            timeRemaining: timer,
            questionPool
        });

        setCurrentQuestion(questionPool[0]);
        setQuestionStartTime(performance.now());
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Timer handling
    useEffect(() => {
        let timer;
        if (selectedMode === 'Exam' && sessionStats.timeRemaining !== null) {
            timer = setInterval(() => {
                setSessionStats(prev => {
                    if (prev.timeRemaining <= 1) {
                        clearInterval(timer);
                        showModal({
                            title: 'Time\'s Up!',
                            message: 'Your exam time has expired. Would you like to see your results?',
                            confirmText: 'See Results',
                            cancelText: 'Cancel',
                            icon: 'warning',
                            onConfirm: () => { closeModal(); resetSession(); },
                            onCancel: closeModal
                        });
                        return prev;
                    }
                    return {
                        ...prev,
                        timeRemaining: prev.timeRemaining - 1
                    };
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [selectedMode, sessionStats.timeRemaining]);

    // Loads user data
    useEffect(() => {
        const loadUserData = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const stats = await fetchUserStats(user.uid);
                setDomainMastery(stats.domainMastery || defaultDomainMastery);
                setLastPracticedByDomain(stats.lastPracticedByDomain || {});

                // Load or migrate ability estimate
                if (stats.abilityEstimate) {
                    setAbilityEstimate(stats.abilityEstimate);
                } else if (stats.totalQuestions > 0) {
                    // Migrate from old delta-rule ability
                    const oldAbility = (stats.correctAnswers / stats.totalQuestions) * 2 - 1;
                    const migrated = migrateFromDeltaRule(oldAbility);
                    setAbilityEstimate(migrated);
                    updateAbilityEstimate(user.uid, migrated);
                }

                if (stats.missedQuestions) {
                    setMissedQuestions(stats.missedQuestions);
                }

                // Load item history for spaced repetition (FSRS)
                const history = await fetchItemHistory(user.uid);
                setItemHistory(history);
                setDueItemCount(countDueItems(history));

                // Check if free user already used their daily quiz
                if (!isPaidUser) {
                    const dailyState = await fetchDailyPracticeState(user.uid);
                    if (dailyState.quizUsed) {
                        setDailyQuizUsed(true);
                    }
                }
            } catch (error) {
                console.error('Error loading user data:', error);
                setError('Failed to load user data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        loadUserData();
    }, [user]);

    // Missed questions mode 
    useEffect(() => {
        const loadMissedQuestions = async () => {
            if (!user) return;

            try {
                const stats = await fetchUserStats(user.uid);
                if (stats.missedQuestions) {
                    setMissedQuestions(stats.missedQuestions);
                }
            } catch (error) {
                console.error('Error loading missed questions:', error);
            }
        };

        loadMissedQuestions();
    }, [user]);


    return (
        <div className="min-h-screen bg-[#070B14] text-gray-100">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/3 h-[520px] w-[820px] rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute bottom-[-220px] right-[-220px] h-[560px] w-[560px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            {/* Hamburger — only visible when sidebar is closed on mobile */}
            {!isSidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="fixed top-4 left-4 z-50 md:hidden rounded-xl bg-white/10 p-2.5 text-white ring-1 ring-white/10 backdrop-blur"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            )}

            <nav
                className={`fixed top-0 left-0 z-40 h-screen w-full md:w-64 overflow-y-auto md:overflow-hidden md:flex md:flex-col border-r border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl ring-1 ring-inset ring-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_40px_-10px_rgba(6,182,212,0.08)] transition-transform duration-200 ease-in-out md:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                    }`}
            >
                <div className="p-6 border-b border-white/10 flex items-start justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-blue-400 mb-2">
                            Security+ Crammer
                        </h1>
                        <div className="text-sm text-white/70">
                            Adaptive Practice
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden rounded-xl bg-white/10 p-2 text-white ring-1 ring-white/10 flex-shrink-0"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="md:flex-grow md:overflow-y-auto p-4">
                    <ul className="space-y-3 mb-6">
                        <li>
                            <Link to="/reports" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10">
                                <ChartBar size={18} className="mr-3" />
                                <span>Reports</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/practice" className="flex items-center px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                                <Target size={18} className="mr-3" />
                                <span>Practice</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/flashcards" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10">
                                <Brain size={18} className="mr-3" />
                                <span>Flashcards</span>
                            </Link>
                        </li>
                    </ul>

                    <div className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                        <h3 className="text-sm font-semibold text-white/55 mb-3">Domain Mastery</h3>
                        {Object.entries(domainMastery).map(([domain, mastery]) => (
                            <div key={domain} className="mb-3">
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs text-white">{domain}</span>
                                    <span className="text-xs text-white">
                                        {Math.min(100, Math.max(0, mastery * 100)).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-1">
                                    <div
                                        className="bg-blue-400 h-1 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.min(100, Math.max(0, mastery * 100))}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 px-6 md:px-4 pb-10 md:pb-4 md:border-t border-white/10 space-y-3">
                    {/* Sidebar upgrade CTA for free users */}
                    {!isPaidUser && (
                        <Link to="/pricing" className="block">
                            <div className="w-full rounded-xl bg-gradient-to-r from-cyan-600/10 to-blue-600/10 ring-1 ring-cyan-500/30 hover:ring-cyan-500/50 px-3 py-2 transition-colors group flex items-center justify-center">
                                <Crown size={14} className="text-cyan-400 mr-2" />
                                <span className="text-xs font-semibold text-cyan-400">Upgrade</span>
                                <span className="text-xs text-white/45 ml-1.5">· Unlock all modes</span>
                            </div>
                        </Link>
                    )}
                    <Link to="/" className="block">
                        <button className="w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-3 py-3 text-white flex items-center justify-center">
                            <ArrowLeft size={18} className="mr-3" />
                            <span className="text-sm">Back to Overview</span>
                        </button>
                    </Link>
                    <button onClick={() => signOut(auth)} className="w-full rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-red-500/30 px-3 py-3 text-red-400 flex items-center justify-center text-sm">
                        <LogOut size={18} className="mr-3" />
                        Sign Out
                    </button>
                </div>
            </nav>

            <main className="relative md:ml-64 p-4 md:p-8">
                <div className="max-w-4xl mx-auto">
                    {!selectedMode ? (
                        <PracticeModeSelection
                            onStartQuiz={startQuizMode}
                            missedQuestionCount={missedQuestions.length}
                            dueItemCount={dueItemCount}
                            dailyQuizUsed={dailyQuizUsed}
                        />
                    ) : (
                        <>
                            <div className="mb-6">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xl font-bold text-blue-400">
                                        {selectedMode === 'Unlimited' ? 'Unlimited Practice' :
                                            selectedMode === 'Quiz' ? 'Quick Quiz' :
                                            selectedMode === 'Review' ? 'Smart Review' :
                                            selectedMode === 'Missed' ? 'Missed Questions' :
                                            selectedMode === 'Domain' ? `${selectedDomain || 'Domain'} Practice` :
                                                'Exam Simulation'}
                                    </h2>
                                    <button
                                        onClick={() => {
                                            showModal({
                                                title: 'End Session?',
                                                message: 'Are you sure you want to end this session? Your progress will be saved.',
                                                confirmText: 'End Session',
                                                cancelText: 'Keep Going',
                                                icon: 'warning',
                                                onConfirm: () => { closeModal(); resetSession(); },
                                                onCancel: closeModal
                                            });
                                        }}
                                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                                    >
                                        <X size={18} />
                                        <span>End Session</span>
                                    </button>
                                </div>
                            </div>

                            {selectedMode === 'Exam' && sessionStats.timeRemaining > 0 && (
                                <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 mb-4">
                                    <div className="flex justify-between items-center">
                                        {isTimerVisible && (
                                            <div className="text-white">
                                                Time Remaining: {formatTime(sessionStats.timeRemaining)}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setIsTimerVisible(!isTimerVisible)}
                                            className="text-white/55 hover:text-white/80 text-sm flex items-center"
                                        >
                                            {isTimerVisible ? (
                                                <>
                                                    <Eye className="h-4 w-4 mr-1" />
                                                    Hide Timer
                                                </>
                                            ) : (
                                                <>
                                                    <EyeOff className="h-4 w-4 mr-1" />
                                                    Show Timer
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Subtle upsell nudge for free users during quiz */}
                            {selectedMode === 'Quiz' && !isPaidUser && (
                                <div className="flex items-center justify-between rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-2.5 mb-4">
                                    <span className="text-xs text-white/45">
                                        {sessionStats.correct}/{sessionStats.total} correct this session
                                    </span>
                                    <Link to="/pricing" className="text-xs text-cyan-400/70 hover:text-cyan-400 transition-colors flex items-center">
                                        <Zap className="h-3 w-3 mr-1" />
                                        Want unlimited? Upgrade
                                    </Link>
                                </div>
                            )}

                            <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 mb-8">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-semibold text-blue-400">
                                        Question {sessionStats.total + 1}
                                    </h3>
                                    <span className="text-sm text-white/55">
                                        {currentQuestion?.domain}
                                    </span>
                                </div>
                                <p className="mb-6 text-white whitespace-pre-line">{currentQuestion?.text?.replace(/<br\s*\/?>/gi, '\n')}</p>
                                <div className="space-y-4">
                                    {currentQuestion?.options.map((option, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleAnswer(index)}
                                            disabled={showNextButton}
                                            className={`w-full text-left p-4 rounded-xl ring-1 ring-white/10
                                    ${!showNextButton ? 'hover:bg-white/10' : ''}
                                    transition-colors text-white
                                    ${showNextButton && index === answeredQuestions[answeredQuestions.length - 1]?.userAnswer
                                                    ? answeredQuestions[answeredQuestions.length - 1]?.correct
                                                        ? 'bg-green-900/50 ring-green-700'
                                                        : 'bg-red-900/50 ring-red-700'
                                                    : ''
                                                }`}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>

                                {showNextButton && (
                                    <div className="mt-6">
                                        {showExplanation && (
                                            <div className={`p-4 rounded-xl mb-4 ${answeredQuestions[answeredQuestions.length - 1]?.correct
                                                ? 'bg-green-900/50 ring-1 ring-green-700'
                                                : 'bg-red-900/50 ring-1 ring-red-700'
                                                }`}>
                                                <div className="font-semibold mb-2">
                                                    {answeredQuestions[answeredQuestions.length - 1]?.correct
                                                        ? 'Correct!'
                                                        : 'Incorrect'}
                                                </div>
                                                <div className="text-sm whitespace-pre-line">
                                                    Explanation: {currentQuestion.explanation?.replace(/<br\s*\/?>/gi, '\n')}
                                                </div>
                                            </div>
                                        )}
                                        <button
                                            onClick={handleNextQuestion}
                                            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white py-3 rounded-xl font-semibold transition-colors"
                                        >
                                            Next Question
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Custom modal */}
            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                onConfirm={modal.onConfirm}
                onCancel={modal.onCancel}
                confirmText={modal.confirmText}
                cancelText={modal.cancelText}
                icon={modal.icon}
            />
        </div>
    );
};

export default PracticeTest;